// src/service/domainUUIDManager.js
import { v4 as uuidv4, validate as validateUUID } from 'uuid';

class DomainUUIDManager {
  constructor() {
    this.domainMap = new Map();
    this.reverseMap = new Map();
    this.storageKey = 'domain_uuid_mapping';
    this.loadFromStorage();
  }

  // Charger depuis le sessionStorage
  loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.domainMap = new Map(parsed.domainMap);
        this.reverseMap = new Map(parsed.reverseMap);
      }
    } catch (error) {
      console.warn('Failed to load domain UUID mapping from storage:', error);
      this.clear();
    }
  }

  // Sauvegarder dans le sessionStorage
  saveToStorage() {
    try {
      const data = {
        domainMap: Array.from(this.domainMap.entries()),
        reverseMap: Array.from(this.reverseMap.entries()),
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save domain UUID mapping to storage:', error);
    }
  }

  // Générer un UUID pour un domaine
  generateDomainUUID(domainId) {
    if (!domainId) return null;

    // Vérifier si on a déjà un UUID pour ce domaine
    if (this.reverseMap.has(domainId.toString())) {
      return this.reverseMap.get(domainId.toString());
    }

    // Générer un nouvel UUID
    const uuid = uuidv4();
    
    // Stocker le mapping dans les deux sens
    this.domainMap.set(uuid, domainId.toString());
    this.reverseMap.set(domainId.toString(), uuid);
    
    // Sauvegarder
    this.saveToStorage();
    
    return uuid;
  }

  // Récupérer l'ID réel depuis l'UUID
  getDomainIdFromUUID(uuid) {
    if (!uuid || !validateUUID(uuid)) {
      return null;
    }
    
    const domainId = this.domainMap.get(uuid);
    
    // Si pas trouvé en mémoire, essayer de recharger depuis le storage
    if (!domainId) {
      this.loadFromStorage();
      return this.domainMap.get(uuid) || null;
    }
    
    return domainId;
  }

  // Récupérer l'UUID depuis l'ID réel
  getUUIDFromDomainId(domainId) {
    if (!domainId) return null;
    return this.reverseMap.get(domainId.toString()) || this.generateDomainUUID(domainId);
  }

  // Nettoyer les mappings expirés (optionnel)
  cleanup() {
    // const now = Date.now();
    // const oneDay = 24 * 60 * 60 * 1000; // 24 heures
    
    // Recharger pour avoir les timestamps
    this.loadFromStorage();
    
    // Ici vous pourriez implémenter une logique de nettoyage basée sur le timestamp
    // Pour l'instant on garde tout pendant la session
  }

  // Vider le cache
  clear() {
    this.domainMap.clear();
    this.reverseMap.clear();
    sessionStorage.removeItem(this.storageKey);
  }

  // Vérifier la validité d'un UUID
  isValidDomainUUID(uuid) {
    return validateUUID(uuid) && this.domainMap.has(uuid);
  }
}

// Instance singleton
export const domainUUIDManager = new DomainUUIDManager();